import React, { useCallback, useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import HPageContainer from "../../../hcomponents/HPageContainer";
import HHeader from "../../../hcomponents/HHeader";
import { showApiMessage } from "../../../utils/messageHandler";
import billingService from "../../../services/billingService";
import { extractCheckoutUrl } from "../../../utils/subscriptionUtils";
import logger from "../../../utils/logger";
import { Button } from "../../../shadcn/ui/button";
import * as localService from "../../../services/localService";
import { CreditCard, User, Building2, AlertCircle, Loader2 } from "lucide-react";

const INVOICE_PAYMENT_REDIRECT_KEY = "invoicePaymentRedirect";

const InvoicePaymentPage = () => {
  const { reference } = useParams();
  const history = useHistory();
  const [status, setStatus] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Load current user details
  const loadCurrentUser = useCallback(() => {
    const user = localService.getUser();
    if (user) {
      setCurrentUser({
        id: user.id,
        username: user.username,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        organizationName: user.organization?.name,
      });
    }
    return user;
  }, []);

  // Fetch invoice details by reference code
  const fetchInvoiceDetails = useCallback(async ref => {
    try {
      const response = await billingService.getInvoices({
        referenceCode: ref,
        limit: 1,
      });

      if (response.data?.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
        return response.data.data[0];
      }
      return null;
    } catch (error) {
      logger.error("Error fetching invoice details:", error);
      return null;
    }
  }, []);

  // Initialize page - check auth and load data
  useEffect(() => {
    const initPage = async () => {
      if (!reference) {
        setStatus("error");
        setErrorMessage("Invalid invoice reference.");
        return;
      }

      // Check if user is authenticated
      const user = loadCurrentUser();
      const isAuthenticated = localService.isAuthenticated();

      if (!isAuthenticated) {
        // User needs to sign in
        setStatus("auth_required");
        return;
      }

      // User is authenticated - fetch invoice details
      const invoice = await fetchInvoiceDetails(reference);

      if (invoice) {
        setInvoiceDetails(invoice);
      }

      setStatus("ready");
    };

    initPage();
  }, [reference, loadCurrentUser, fetchInvoiceDetails]);

  const redirectToSignin = useCallback(() => {
    history.replace("/signin");
  }, [history]);

  const startPayment = useCallback(async () => {
    if (!reference) {
      return;
    }

    setIsProcessingPayment(true);
    setStatus("processing");

    try {
      const response = await billingService.initiatePayment({ reference });
      const checkoutUrl = extractCheckoutUrl(response);

      if (!checkoutUrl) {
        throw new Error("Unable to start payment. Checkout URL not provided by billing service.");
      }

      // Store redirect info for returning after payment
      try {
        localStorage.setItem(
          INVOICE_PAYMENT_REDIRECT_KEY,
          JSON.stringify({
            redirectTo: "/host/invoice",
            source: "pay-invoice",
            reference,
            createdAt: new Date().toISOString(),
          })
        );
      } catch (storageError) {
        logger.warn("Unable to cache invoice payment redirect flag", storageError);
      }

      // Redirect to payment gateway
      window.location.href = checkoutUrl;
    } catch (error) {
      try {
        localStorage.removeItem(INVOICE_PAYMENT_REDIRECT_KEY);
      } catch (_) {}

      const message = error?.response?.data?.message || error?.message || "Failed to initiate payment.";
      setErrorMessage(message);
      setStatus("error");
      showApiMessage(error, "initiate payment");
      setIsProcessingPayment(false);
    }
  }, [reference]);

  const handleSignIn = () => {
    if (!reference) {
      redirectToSignin();
      return;
    }

    const resumePath = `/pay-invoice/${reference}`;

    try {
      localStorage.setItem(
        INVOICE_PAYMENT_REDIRECT_KEY,
        JSON.stringify({
          redirectTo: "/signin",
          source: "pay-invoice",
          reference,
          resumePath,
          createdAt: new Date().toISOString(),
        })
      );
    } catch (storageError) {
      logger.warn("Unable to cache invoice payment login flag", storageError);
    }

    history.push(`/signin?redirect=${encodeURIComponent(resumePath)}`);
  };

  const handleRetry = () => {
    setErrorMessage("");
    startPayment();
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (!currentUser) return "";
    if (currentUser.firstName && currentUser.lastName) {
      return `${currentUser.firstName} ${currentUser.lastName}`;
    }
    return currentUser.username || currentUser.email || "User";
  };

  // Format currency amount
  const formatAmount = (amount, currency) => {
    if (!amount) return "";
    const numAmount = parseFloat(amount);
    const currencyCode = currency?.code || "KES";
    return `${currencyCode} ${numAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <HPageContainer>
      <HHeader />
      <div className="overflow-auto p-6 mx-3 mb-3 bg-white rounded-2xl border border-gray-200 max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Pay Invoice</h1>
            <p className="text-sm text-gray-500">
              Reference: <span className="font-mono">{reference}</span>
            </p>
          </div>
        </div>

        {/* Loading State */}
        {status === "loading" && (
          <div className="py-16 flex flex-col items-center text-center">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-4" />
            <h2 className="text-lg font-medium text-gray-900 mb-2">Loading invoice details…</h2>
            <p className="text-sm text-gray-600">Please wait while we fetch your invoice.</p>
          </div>
        )}

        {/* Auth Required State */}
        {status === "auth_required" && (
          <div className="py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Sign in required</h2>
            <p className="text-sm text-gray-600 max-w-lg mx-auto mb-6">
              You need to sign in to verify your account before paying this invoice.
            </p>
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={redirectToSignin}>
                Go to sign in
              </Button>
              <Button onClick={handleSignIn}>Sign in to pay</Button>
            </div>
          </div>
        )}

        {/* Ready to Pay State */}
        {status === "ready" && currentUser && (
          <div className="space-y-6">
            {/* Invoice Details Card */}
            {invoiceDetails && (
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Invoice Amount</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatAmount(invoiceDetails.amount, invoiceDetails.currency)}
                    </p>
                  </div>
                  {invoiceDetails.status && (
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        invoiceDetails.status === "PENDING"
                          ? "bg-amber-100 text-amber-700"
                          : invoiceDetails.status === "PAID"
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {invoiceDetails.status}
                    </span>
                  )}
                </div>

                {invoiceDetails.description && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Description</p>
                    <p className="text-sm text-gray-700">{invoiceDetails.description}</p>
                  </div>
                )}
              </div>
            )}

            {/* Pay As User Card */}
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-blue-600 uppercase tracking-wider mb-1">Paying as</p>
                  <p className="font-semibold text-gray-900">{getUserDisplayName()}</p>
                  {currentUser.email && <p className="text-sm text-gray-600">{currentUser.email}</p>}
                  {currentUser.organizationName && (
                    <div className="flex items-center gap-1 mt-1">
                      <Building2 className="w-3 h-3 text-gray-500" />
                      <p className="text-xs text-gray-500">{currentUser.organizationName}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Pay Button */}
            <div className="flex flex-col gap-3">
              <Button onClick={startPayment} disabled={isProcessingPayment} className="w-full py-6 text-lg" size="lg">
                {isProcessingPayment ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing…
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay {invoiceDetails ? formatAmount(invoiceDetails.amount, invoiceDetails.currency) : "Now"}
                  </>
                )}
              </Button>

              {!invoiceDetails && (
                <p className="text-xs text-gray-500 text-center">
                  Invoice details not available. Click Pay to proceed with reference: {reference}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Processing State */}
        {status === "processing" && (
          <div className="py-16 flex flex-col items-center text-center">
            <div className="mb-6 w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            <h2 className="text-lg font-medium text-gray-900 mb-2">Preparing secure checkout…</h2>
            <p className="text-sm text-gray-600 max-w-md">
              We are redirecting you to our payment partner. Please keep this tab open until the page changes.
            </p>
          </div>
        )}

        {/* Error State */}
        {status === "error" && (
          <div className="py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-lg font-semibold text-red-600 mb-2">Payment could not be started</h2>
            <p className="text-sm text-gray-600 max-w-lg mx-auto mb-6">{errorMessage}</p>
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={redirectToSignin}>
                Go to sign in
              </Button>
              <Button onClick={handleRetry}>Try again</Button>
            </div>
          </div>
        )}
      </div>
    </HPageContainer>
  );
};

export default InvoicePaymentPage;
